import { DateOnly } from '@if/core/date'
import { ESourceSystem } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Ban } from '../../types'

export interface BanishmentFilterDto {
  firstName?: string
  lastName?: string
  birthDate?: string
  sin?: string
  sourceSystem?: string
  offset?: number
  limit?: number
}

interface BanishmentDto {
  id: string
  firstName: string
  lastName: string
  birthDate: string
  sin: string
  reason: string
  expirationDate: string | null
  sourceSystem: ESourceSystem | ''
}

export interface BanishmentCreateDto {
  firstName: string
  lastName: string
  birthDate: DateOnly
  sin: string
  reason: string
  expirationDate: DateOnly | null
}

export interface UpdateExpirationDateDto {
  newExpirationDate: DateOnly | null
}

const transformBanishmentDtoToBO = (dto: BanishmentDto): Ban => ({
  ...dto,
  birthDate: new DateOnly(dto.birthDate),
  expirationDate: dto.expirationDate ? new DateOnly(dto.expirationDate) : null,
})

const baseUrl = '/banishments'

export async function createBanishment(banishmentDto: BanishmentCreateDto): Promise<Ban> {
  const apiClient = getApiClient()
  const response = await apiClient.post<Ban>(baseUrl, banishmentDto)
  return response.data
}

export async function unbanApplicant(banishmentId: string): Promise<void> {
  const apiClient = getApiClient()
  await apiClient.delete(`${baseUrl}/${banishmentId}`)
}

export async function updateExpirationDate(
  banishmentId: string,
  newExpirationDate: UpdateExpirationDateDto,
): Promise<Ban> {
  const apiClient = getApiClient()
  const response = await apiClient.put<BanishmentDto>(`${baseUrl}/${banishmentId}/expiration`, newExpirationDate)
  return transformBanishmentDtoToBO(response.data)
}

export async function getAllBanishments(): Promise<Ban[]> {
  const apiClient = getApiClient()
  const response = await apiClient.get<Ban[]>(baseUrl)
  return response.data
}

export async function searchBanishments(filterDto: BanishmentFilterDto): Promise<Ban[]> {
  const apiClient = getApiClient()
  const response = await apiClient.post<BanishmentDto[]>(`${baseUrl}/search`, filterDto)
  return response.data.map(transformBanishmentDtoToBO)
}

export function useCreateBanishment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createBanishment,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['banishments'] })
    },
  })
}

export function useUnbanApplicant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: unbanApplicant,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['banishments'] })
    },
  })
}

export function useUpdateExpirationDate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      banishmentId,
      newExpirationDate,
    }: {
      banishmentId: string
      newExpirationDate: UpdateExpirationDateDto
    }) => updateExpirationDate(banishmentId, newExpirationDate),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['banishments'] })
    },
  })
}

export function useSearchBanishments(filterDto: BanishmentFilterDto) {
  return useQuery<Ban[], AxiosError>({
    queryKey: ['banishments', 'search', filterDto],
    queryFn: () => searchBanishments(filterDto),
    staleTime: 0,
    gcTime: 0,
    placeholderData: [],
  })
}
